import { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { setTenantGroup, setUserProfileProperties } from '../services/analytics';
import userService from '../services/userService';
import { setAuthToken } from '../services/api';
import { tenantService } from '../services/tenantService';
import { TENANT_INACTIVE_EVENT, type TenantInactiveDetail } from '../constants/authEvents';

/** Clerk's UserResource type, derived from the useUser hook to avoid internal package imports. */
export type ClerkUser = NonNullable<ReturnType<typeof useUser>['user']>;

export type BackendProfile = {
  id: string;
  tenantId?: string;
  role?: string;
  stateCode?: string;
  licenseNumber?: string;
  licenseIsActive?: boolean;
  educationLevel?: string;
  major?: string;
  graduationDate?: string;
  birthDate?: string | null;
  hasSeenWelcomeVideo?: boolean;
  onboardingCompletedAt?: string | null;
};

interface UserState {
  // Clerk data
  clerkUser: ClerkUser | null;
  isLoaded: boolean;
  isSignedIn: boolean;
  
  // Backend data  
  backendProfile: BackendProfile | null;
  loading: boolean;
  
  // Computed roles
  isAdmin: boolean;
  isTenantAdmin: boolean;

  // Tenant access gate state from backend middleware responses
  tenantAccess: 'unknown' | 'active' | 'inactive';
  tenantAccessMessage: string | null;
}

const VISIBILITY_REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

// Simple state manager using module-level variables and event emitter
class UserStore extends EventTarget {
  private state: UserState = {
    clerkUser: null,
    isLoaded: false,
    isSignedIn: false,
    backendProfile: null,
    loading: true,
    isAdmin: false,
    isTenantAdmin: false,
    tenantAccess: 'unknown',
    tenantAccessMessage: null,
  };

  private hiddenAt = 0;

  constructor() {
    super();
    console.log('🚀 User store initialized');

    // Re-fetch profile when the user returns to the tab after a long absence.
    // This ensures a removed-from-tenant user sees their updated state without
    // requiring a full page reload.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.hiddenAt = Date.now();
        } else if (
          document.visibilityState === 'visible' &&
          this.hiddenAt > 0 &&
          Date.now() - this.hiddenAt >= VISIBILITY_REFRESH_THRESHOLD_MS &&
          this.state.isSignedIn &&
          this.state.clerkUser
        ) {
          void this.refreshUser();
        }
      });
    }

    if (typeof window !== 'undefined') {
      window.addEventListener(TENANT_INACTIVE_EVENT, this.handleTenantInactiveEvent as EventListener);
    }
  }

  private handleTenantInactiveEvent = (event: Event) => {
    const customEvent = event as CustomEvent<TenantInactiveDetail>;
    this.markTenantInactive(customEvent.detail?.message);
  };

  getState() {
    return { ...this.state };
  }

  private setState(updates: Partial<UserState>) {
    this.state = { ...this.state, ...updates };
    this.dispatchEvent(new CustomEvent('state-change', { detail: this.state }));
  }

  /**
   * Update Clerk-derived state (user object, roles from publicMetadata) without
   * triggering a backend profile fetch.  Called internally by refreshUser() so
   * it can sequence the reload → role update → single fetch itself.
   */
  private updateClerkState(user: ClerkUser | null, isLoaded: boolean, isSignedIn: boolean) {
    console.log('🔐 Setting Clerk auth data:', { user: !!user, isLoaded, isSignedIn });

    // Compute roles from Clerk publicMetadata
    const roles = (user?.publicMetadata?.roles as string[]) || [];
    const isAdmin = roles.includes('admin');
    const isTenantAdmin = isAdmin || roles.includes('tenant-admin');

    console.log('🎭 Role Computation:', {
      clerkPublicMetadata: user?.publicMetadata,
      roles,
      isAdmin,
      isTenantAdmin
    });

    this.setState({
      clerkUser: user,
      isLoaded,
      isSignedIn,
      isAdmin,
      isTenantAdmin,
      tenantAccess: isSignedIn ? this.state.tenantAccess : 'unknown',
      tenantAccessMessage: isSignedIn ? this.state.tenantAccessMessage : null,
    });

    if (!isSignedIn) {
      setAuthToken(null);
    }
  }

  setClerkAuth(user: ClerkUser | null, isLoaded: boolean, isSignedIn: boolean) {
    this.updateClerkState(user, isLoaded, isSignedIn);

    // Fetch backend profile if user is signed in
    if (isSignedIn && user) {
      this.fetchUserProfile(user.id);
    } else {
      this.setState({
        backendProfile: null,
        loading: false,
        tenantAccess: 'unknown',
        tenantAccessMessage: null,
      });
    }
  }

  async fetchUserProfile(userId: string) {
    try {
      this.setState({ loading: true });
      console.log('👤 Fetching backend profile for:', userId);
      
      const profile = await userService.getMyProfile();
      console.log('👤 Backend Profile:', profile);

      // Recompute roles: merge Clerk metadata with the backend profile role.
      // The backend role is authoritative and may be ahead of Clerk metadata
      // propagation (e.g. right after business signup promotes to tenant-admin).
      const clerkRoles = (this.state.clerkUser?.publicMetadata?.roles as string[]) || [];
      const backendRole = profile?.role;
      const allRoles = new Set(clerkRoles);
      if (backendRole) allRoles.add(backendRole);
      const isAdmin = allRoles.has('admin');
      const isTenantAdmin = isAdmin || allRoles.has('tenant-admin');

      this.setState({
        backendProfile: profile,
        loading: false,
        tenantAccess: 'active',
        tenantAccessMessage: null,
        isAdmin,
        isTenantAdmin,
      });
      tenantService.setTenantId(profile?.tenantId || null);
      if (profile) {
        setUserProfileProperties({
          role: profile.role,
          stateCode: profile.stateCode,
          tenantId: profile.tenantId,
        });
      }
      if (profile?.tenantId) {
        setTenantGroup(profile.tenantId);
        // Enrich the tenant group with a readable name (cached lookup; best-effort)
        tenantService
          .getTenantById(profile.tenantId)
          .then((tenant) => {
            if (tenant?.name) {
              setTenantGroup(profile.tenantId!, { name: tenant.name });
            }
          })
          .catch(() => {
            /* group stays id-only if the lookup fails */
          });
      }
      return profile;
    } catch (error) {
      console.warn('⚠️ Failed to fetch user profile (backend may be unavailable):', error);
      this.setState({ backendProfile: null, loading: false });
      return null;
    }
  }

  markTenantInactive(message?: string) {
    const tenantInactiveMessage =
      message || "Your organization's account is currently inactive. Please contact your administrator.";

    this.setState({
      backendProfile: null,
      loading: false,
      tenantAccess: 'inactive',
      tenantAccessMessage: tenantInactiveMessage,
    });

    tenantService.setTenantId(null);
  }

  async refreshUser() {
    if (this.state.clerkUser) {
      // Reload Clerk user to pick up server-side metadata changes (e.g. role
      // promotions), then recompute Clerk-derived roles. Uses updateClerkState
      // (not setClerkAuth) to avoid a duplicate fetchUserProfile call.
      await this.state.clerkUser.reload();
      this.updateClerkState(this.state.clerkUser, this.state.isLoaded, this.state.isSignedIn);

      return this.fetchUserProfile(this.state.clerkUser.id);
    }

    return this.state.backendProfile;
  }

  updateClerkUserName(firstName: string, lastName: string) {
    if (!this.state.clerkUser) return;
    this.setState({
      clerkUser: { ...this.state.clerkUser, firstName, lastName },
    });
  }

  reset() {
    console.log('🔄 Resetting user store');
    setAuthToken(null);
    tenantService.setTenantId(null);
    this.setState({
      clerkUser: null,
      isLoaded: false,
      isSignedIn: false,
      backendProfile: null,
      loading: true,
      isAdmin: false,
      isTenantAdmin: false,
      tenantAccess: 'unknown',
      tenantAccessMessage: null,
    });
  }
}

// Create global instance
export const userStore = new UserStore();

// React hook to use the store
export const useUserStore = () => {
  const [state, setState] = useState(userStore.getState());

  useEffect(() => {
    const handleStateChange = (event: CustomEvent) => {
      setState(event.detail);
    };

    userStore.addEventListener('state-change', handleStateChange as EventListener);
    return () => userStore.removeEventListener('state-change', handleStateChange as EventListener);
  }, []);

  return {
    ...state,
    setClerkAuth: userStore.setClerkAuth.bind(userStore),
    fetchUserProfile: userStore.fetchUserProfile.bind(userStore),
    refreshUser: userStore.refreshUser.bind(userStore),
    updateClerkUserName: userStore.updateClerkUserName.bind(userStore),
    reset: userStore.reset.bind(userStore),
  };
};